import { ButtonView, GridView } from '@vivli/shared/views';
import { CSSProperties, useMemo } from 'react';
import { ColDef } from 'ag-grid-community';
import { useFormContext, useWatch } from 'react-hook-form';
import { GridCellRendererEnum } from '@vivli/shared/infrastructure/enum';
import { IDisclosure, IStudyDisclosureDetails, IStudyDisclosureDetailsDto } from '@vivli/features/disclosures/infrastructure/interface';
import { useDisclosureContext } from '@vivli/features/disclosures/infrastructure/context';

const containerStyle: CSSProperties = {
    display: 'grid',
    gridTemplateRows: 'auto 1fr',
    rowGap: '1em',
    height: '100%',
};

const buttonContainerStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '150px 150px',
    columnGap: '1em',
    justifyContent: 'end',
    alignItems: 'center',
};

const bodyStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    columnGap: '2em',
};

export const StudiesUsedFormView = () => {
    const { permissions, dataRequest, disclosure } = useDisclosureContext();
    const formApi = useFormContext<IDisclosure>();
    const studies: IStudyDisclosureDetails[] = useWatch({ name: 'studyDisclosureDetails' });

    const studyList = useMemo(() => {
        return dataRequest?.requestedStudies.map<IStudyDisclosureDetailsDto>(
            ({ studyTitle, studyId, sponsorProtocolId, nctId, organizationName, organizationId }) => {
                const inDisclosure = studies?.some((s) => s.studyId === studyId);

                return {
                    studyId,
                    disclosureId: disclosure?.id || 0,
                    sponsorId: sponsorProtocolId,
                    nctId: nctId,
                    organizationName: organizationName,
                    organizationId: organizationId,
                    inDisclosure,
                    title: studyTitle,
                };
            }
        );
    }, [dataRequest?.requestedStudies, studies]);

    const setSelectedStudies = (selectAllStudies: boolean) => {
        if (selectAllStudies) {
            const updatedStudies = dataRequest?.requestedStudies.map<IStudyDisclosureDetails>(({ studyId, organizationId }) => ({
                studyId,
                disclosureId: disclosure.id,
                organizationId,
            }));
            formApi.setValue('studyDisclosureDetails', updatedStudies, { shouldDirty: true, shouldValidate: true });
        } else {
            formApi.setValue('studyDisclosureDetails', [], { shouldDirty: true, shouldValidate: true });
        }
    };

    const updateStudyInDisclosure = (inDisclosure: boolean, dto: IStudyDisclosureDetailsDto) => {
        const formOptions = { shouldDirty: true, shouldValidate: true };
        if (!inDisclosure) {
            // remove the study from the list
            const filteredStudies = studies?.filter((s) => s.studyId !== dto.studyId);
            formApi.setValue('studyDisclosureDetails', filteredStudies, formOptions);
        } else {
            // add the study to the list
            formApi.setValue(
                'studyDisclosureDetails',
                [
                    ...studies,
                    {
                        organizationId: dto.organizationId,
                        studyId: dto.studyId,
                        disclosureId: dto.disclosureId,
                    },
                ],
                formOptions
            );
        }
        return true;
    };

    const gridCols: ColDef[] = [
        {
            headerName: '',
            field: 'inDisclosure',
            floatingFilter: false,
            filter: false,
            suppressMovable: true,
            sortable: false,
            cellRenderer: GridCellRendererEnum.GridCheckbox,
            cellRendererParams: ({ data }) => ({
                readonly: !permissions.editStudies,
                onChange: (checked: boolean) => updateStudyInDisclosure(checked, data),
            }),
            maxWidth: 50,
        },
        {
            headerName: 'NCT ID',
            field: 'nctId',
            maxWidth: 150,
            valueFormatter: ({ value }) => {
                if (!value || value?.length <= 0) {
                    return 'N/A';
                }

                return value;
            },
        },
        {
            headerName: 'Sponsor ID',
            field: 'sponsorId',
            maxWidth: 150,
            valueFormatter: ({ value }) => {
                if (!value || value?.length <= 0) {
                    return 'N/A';
                }

                return value;
            },
        },
        {
            headerName: 'Data Contributor',
            field: 'organizationName',
            sort: 'asc',
            maxWidth: 250,
            cellRenderer: GridCellRendererEnum.GridCellLongText,
            wrapText: true,
        },
        {
            headerName: 'Title',
            field: 'title',
            cellRenderer: GridCellRendererEnum.GridCellLongText,
            wrapText: true,
        },
    ];

    const grid = (
        <GridView
            rowData={studyList}
            columns={gridCols}
            hideHeader={false}
            hideFloatingFilter={false}
            fullWidthColumns={true}
            // onRowClick={onRowClick}
            emptyMessage={'No studies found.'}
            autoHeight={true}
        />
    );

    return (
        <div style={containerStyle}>
            {permissions.editStudies && (
                <div style={bodyStyle}>
                    <div>
                        Please review the list of requested studies and uncheck any studies that were not included in preparing the analysis
                        for this disclosure. If you did not utilize a study for this public disclosure, please confirm in the open chat if
                        you plan to submit more public disclosures using these studies based on other Data Contributor studies, as outlined
                        in your research plan.
                    </div>
                    <div style={buttonContainerStyle}>
                        <ButtonView onClick={() => setSelectedStudies(true)}>Include All Studies</ButtonView>
                        <ButtonView onClick={() => setSelectedStudies(false)}>Remove All Studies</ButtonView>
                    </div>
                </div>
            )}
            <div>{grid}</div>
        </div>
    );
};
